import {
  LINE_RICH_MENU_IMAGE_MAX_BYTES,
  LINE_RICH_MENU_IMAGE_MAX_WIDTH,
  LINE_RICH_MENU_IMAGE_MIN_ASPECT_RATIO_DENOMINATOR,
  LINE_RICH_MENU_IMAGE_MIN_ASPECT_RATIO_NUMERATOR,
  LINE_RICH_MENU_IMAGE_MIN_HEIGHT,
  LINE_RICH_MENU_IMAGE_MIN_WIDTH,
  richMenuImageContentTypeFromMime,
  ERichMenuImageContentType,
} from 'types'

export type TRichMenuImageValidationResult =
  | {
      ok: true
      contentType: ERichMenuImageContentType
      width: number
      height: number
    }
  | {
      ok: false
      reason:
        | 'IMAGE_TOO_LARGE'
        | 'UNSUPPORTED_FORMAT'
        | 'UNREADABLE_IMAGE'
        | 'INVALID_DIMENSIONS'
      width?: number
      height?: number
    }

const PNG_SIGNATURE = [137, 80, 78, 71, 13, 10, 26, 10]

/**
 * 実データから検出したMIMEを、S3署名取得にも使えるFileへ反映する。
 */
export function normalizeRichMenuImageFileContentType(
  file: File,
  contentType: ERichMenuImageContentType,
): File {
  if (file.type === contentType) return file

  return new File([file], file.name, {
    type: contentType,
    lastModified: file.lastModified,
  })
}

/**
 * File.typeや拡張子ではなく、画像先頭のsignatureからLINE送信用MIMEを判定する。
 */
export function readRichMenuImageContentType(
  file: File,
): Promise<ERichMenuImageContentType | null> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => {
      if (!(reader.result instanceof ArrayBuffer)) {
        reject(new Error('Failed to read image signature'))
        return
      }

      const bytes = new Uint8Array(reader.result)
      const isPng =
        bytes.length >= PNG_SIGNATURE.length &&
        PNG_SIGNATURE.every((byte, index) => bytes[index] === byte)
      if (isPng) {
        resolve(ERichMenuImageContentType.Png)
        return
      }

      if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
        resolve(ERichMenuImageContentType.Jpeg)
        return
      }

      resolve(null)
    }
    reader.onerror = () => reject(new Error('Failed to read image signature'))
    reader.onabort = () => reject(new Error('Image signature read aborted'))
    reader.readAsArrayBuffer(file.slice(0, PNG_SIGNATURE.length))
  })
}

/**
 * Browserで画像をdecodeし、実際のpixel寸法を取得する。
 */
export function readRichMenuImageDimensions(
  file: File,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    let objectUrl: string
    try {
      objectUrl = URL.createObjectURL(file)
    } catch {
      reject(new Error('Failed to create image object URL'))
      return
    }

    const image = new Image()
    const cleanup = () => URL.revokeObjectURL(objectUrl)

    image.onload = () => {
      const dimensions = {
        width: image.naturalWidth,
        height: image.naturalHeight,
      }
      cleanup()
      resolve(dimensions)
    }
    image.onerror = () => {
      cleanup()
      reject(new Error('Failed to decode image'))
    }
    image.src = objectUrl
  })
}

/**
 * LINE rich-menu画像のbyte数、形式、pixel寸法を検証する。
 */
export function validateRichMenuImageMetadata(params: {
  byteLength: number
  mime: string
  width: number
  height: number
}): TRichMenuImageValidationResult {
  if (params.byteLength > LINE_RICH_MENU_IMAGE_MAX_BYTES) {
    return { ok: false, reason: 'IMAGE_TOO_LARGE' }
  }

  const contentType = richMenuImageContentTypeFromMime(params.mime)
  if (!contentType) {
    return { ok: false, reason: 'UNSUPPORTED_FORMAT' }
  }

  const dimensionsAreValid =
    Number.isSafeInteger(params.width) &&
    Number.isSafeInteger(params.height) &&
    params.width >= LINE_RICH_MENU_IMAGE_MIN_WIDTH &&
    params.width <= LINE_RICH_MENU_IMAGE_MAX_WIDTH &&
    params.height >= LINE_RICH_MENU_IMAGE_MIN_HEIGHT &&
    params.width * LINE_RICH_MENU_IMAGE_MIN_ASPECT_RATIO_DENOMINATOR >=
      params.height * LINE_RICH_MENU_IMAGE_MIN_ASPECT_RATIO_NUMERATOR

  if (!dimensionsAreValid) {
    return {
      ok: false,
      reason: 'INVALID_DIMENSIONS',
      width: params.width,
      height: params.height,
    }
  }

  return {
    ok: true,
    contentType,
    width: params.width,
    height: params.height,
  }
}

/**
 * 選択された画像をdecodeし、LINE rich-menuへ送信可能か検証する。
 */
export async function validateRichMenuImageFile(
  file: File,
): Promise<TRichMenuImageValidationResult> {
  if (file.size > LINE_RICH_MENU_IMAGE_MAX_BYTES) {
    return { ok: false, reason: 'IMAGE_TOO_LARGE' }
  }

  try {
    const contentType = await readRichMenuImageContentType(file)
    if (!contentType) {
      return { ok: false, reason: 'UNSUPPORTED_FORMAT' }
    }

    const dimensions = await readRichMenuImageDimensions(file)
    return validateRichMenuImageMetadata({
      byteLength: file.size,
      mime: contentType,
      ...dimensions,
    })
  } catch {
    return { ok: false, reason: 'UNREADABLE_IMAGE' }
  }
}
